import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { TransactionTitle } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

/**
 * CAP-1975: maps a transaction's legal/beneficial `title` (the nominee-structure interest)
 * to the label shown in the Transactions table "Interest" column.
 *
 * A transaction with no `title` either predates the nominee feature or sits on a non-nominee
 * security — there is no meaningful interest to show, so we return `null` and the caller renders
 * a dash. Note "Split" (from the design) is a paired-issue concept spanning two transactions and
 * cannot be derived from a single row's title, so it is intentionally not produced here.
 */
export const getTransactionInterestText = (
	title: TransactionTitle | undefined | null,
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>
): string | null => {
	switch (title) {
		case TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE:
			return tCapManage('transactions.interest.both', { defaultValue: 'Both' });
		case TransactionTitle.LEGAL_TITLE:
			return tCapManage('transactions.interest.legal', { defaultValue: 'Legal' });
		case TransactionTitle.BENEFICIAL_INTEREST:
			return tCapManage('transactions.interest.beneficial', {
				defaultValue: 'Beneficial',
			});
		default:
			return null;
	}
};

/**
 * CAP-1975: selectable interest options for a nominee TRANSFER (create/edit drawers). Covers the
 * three interests a user can move on a transfer — beneficial only, legal only, or both rails —
 * matching the CAP-1975 spec's "interest being transferred" input.
 */
export const getTransferInterestOptions = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>
): { value: TransactionTitle; label: string }[] => [
	{
		value: TransactionTitle.BENEFICIAL_INTEREST,
		label: tCapManage('transactions.interest.beneficial', { defaultValue: 'Beneficial' }),
	},
	{
		value: TransactionTitle.LEGAL_TITLE,
		label: tCapManage('transactions.interest.legal', { defaultValue: 'Legal' }),
	},
	{
		value: TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE,
		label: tCapManage('transactions.interest.both', { defaultValue: 'Both' }),
	},
];
